import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('access_key', '3ecf27f1-0b06-493f-8a17-94c13339fed3');
      formBody.append('subject', 'New Quote Request - Homepage Contact Form');
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('service', formData.service);
      formBody.append('message', formData.message);

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section className="py-16 md:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
            Get Your Free Quote Today
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Fill out the form below and we'll get back to you within 24 hours
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-10">
              <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6" data-readdy-form id="contact-form">
                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-[#535669] mb-2">
                    Full Name <span className="text-[#f67f19]">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-5 py-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#f67f19] focus:border-[#f67f19] text-base transition-all"
                    placeholder="John Smith"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-bold text-[#535669] mb-2">
                      Email Address <span className="text-[#f67f19]">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#f67f19] focus:border-[#f67f19] text-base transition-all"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-bold text-[#535669] mb-2">
                      Phone Number <span className="text-[#f67f19]">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#f67f19] focus:border-[#f67f19] text-base transition-all"
                      placeholder="0416 497 716"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-bold text-[#535669] mb-2">
                    Service Required <span className="text-[#f67f19]">*</span>
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-5 py-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#f67f19] focus:border-[#f67f19] text-base cursor-pointer transition-all"
                  >
                    <option value="">Select a service</option>
                    <option value="Emergency Work">Emergency Work</option>
                    <option value="Ceiling Fans">Ceiling Fans</option>
                    <option value="Power Points">Power Points</option>
                    <option value="Fault Detection">Fault Detection</option>
                    <option value="Smoke Alarms">Smoke Alarms</option>
                    <option value="Lighting">Lighting</option>
                    <option value="Electrical Repairs">Electrical Repairs</option>
                    <option value="Wiring & Rewiring">Wiring & Rewiring</option>
                    <option value="Surge Protection">Surge Protection</option>
                    <option value="Switchboards">Switchboards</option>
                    <option value="Electrical Maintenance">Electrical Maintenance</option>
                    <option value="Solar Batteries">Solar Batteries</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-bold text-[#535669] mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    maxLength={500}
                    className="w-full px-5 py-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#f67f19] focus:border-[#f67f19] text-base resize-none transition-all"
                    placeholder="Tell us about your electrical needs..."
                  ></textarea>
                  <div className="text-sm text-gray-500 mt-2">{formData.message.length}/500 characters</div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#f67f19] text-white px-8 py-5 rounded-full font-bold text-lg hover:bg-[#e06e0a] transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer transform hover:scale-[1.02]"
                >
                  {isSubmitting ? 'Sending...' : 'Get Your Free Quote Now'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border-2 border-green-500 text-green-800 px-6 py-4 rounded-lg flex items-center gap-3">
                    <i className="ri-checkbox-circle-fill text-2xl text-green-500"></i>
                    <span className="font-semibold">Thank you! We'll contact you within 24 hours.</span>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border-2 border-red-500 text-red-800 px-6 py-4 rounded-lg flex items-center gap-3">
                    <i className="ri-error-warning-fill text-2xl text-red-500"></i>
                    <span className="font-semibold">Something went wrong. Please try again or call us directly.</span>
                  </div>
                )}
              </form>
            </div>
          </div>

          <div className="space-y-5 md:space-y-6">
            <div className="bg-white rounded-xl p-5 md:p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-start">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-orange-100 flex items-center justify-center mr-3 md:mr-4 flex-shrink-0">
                  <i className="ri-phone-fill text-orange-600 text-lg md:text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">Phone</h3>
                  <a href="tel:0416497716" className="text-[#f67f19] hover:text-white transition-colors text-lg cursor-pointer">
                    0416 497 716
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 md:p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-start">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-orange-100 flex items-center justify-center mr-3 md:mr-4 flex-shrink-0">
                  <i className="ri-mail-fill text-orange-600 text-lg md:text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">Email</h3>
                  <a href="mailto:urbanconnect@mail.com" className="text-[#f67f19] hover:text-white transition-colors break-all cursor-pointer">
                    urbanconnect@mail.com
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 md:p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-start">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-orange-100 flex items-center justify-center mr-3 md:mr-4 flex-shrink-0">
                  <i className="ri-time-fill text-orange-600 text-lg md:text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">Business Hours</h3>
                  <div className="text-gray-300 space-y-1">
                    <div>Mon - Fri: 7:00 AM - 6:00 PM</div>
                    <div>Sat: 8:00 AM - 4:00 PM</div>
                    <div>Sun: Emergency Only</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 md:p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-start">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-orange-100 flex items-center justify-center mr-3 md:mr-4 flex-shrink-0">
                  <i className="ri-customer-service-2-fill text-orange-600 text-lg md:text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">24/7 Emergency</h3>
                  <div className="text-gray-300">
                    Available for urgent electrical issues
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 md:p-8 text-white">
              <h3 className="text-xl md:text-2xl font-bold mb-2 md:mb-3">Special Offer!</h3>
              <p className="text-base md:text-lg mb-4 md:mb-6">
                Get $50 OFF your first service + FREE call out
              </p>
              <div className="flex items-center text-sm md:text-base">
                <i className="ri-time-line text-xl md:text-2xl mr-2 md:mr-3"></i>
                <span>Limited time offer - Book now!</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}